import { z } from 'zod';

import type { AnalyticsContext } from './context';
import { buildFeatureFlagEvaluatedEvent, type ProductEvent } from './events';
import { flagKeySchema, variantKeySchema, type FeatureFlagKey } from './ids';

const featureFlagDefinitionSchema = z
  .object({
    key: flagKeySchema,
    display_name: z.string().min(4).max(120),
    active_on_create: z.boolean(),
    default_value: z.union([z.boolean(), variantKeySchema]),
    internal_only: z.boolean(),
    variants: z.array(variantKeySchema).min(2).optional(),
  })
  .strict();

export type FeatureFlagDefinition = z.infer<typeof featureFlagDefinitionSchema>;

export const analyticsDiagnosticsFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-analytics-diagnostics',
  display_name: 'Analytics diagnostics internal flag',
  active_on_create: true,
  default_value: false,
  internal_only: true,
  variants: ['control', 'variant'],
});

export const careCallFollowupFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-care-call-followup',
  display_name: 'Post-care-call follow-up (recap and next actions)',
  active_on_create: true,
  default_value: false,
  internal_only: false,
});

// Server-authoritative kill-switch for NEW rewards enrolments. Default-on: a
// PostHog outage or absent config resolves to `true` so a provider problem can
// never take enrolment down; only an explicit `false` refuses new enrolments
// (already-enrolled members keep earning). Internal-only (a server-side
// decision, never a client fetch).
export const rewardsEnrolmentFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-rewards-enrolment',
  display_name: 'Rewards enrolment kill-switch',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});

// The first delivery contains only the first three review screens. Keep member
// entry fail-closed until the later continuation is ready for rollout.
export const initialReviewEntryFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-initial-review-entry',
  display_name: 'Initial review entry visibility flag',
  active_on_create: false,
  default_value: false,
  internal_only: true,
});

/**
 * Kill switch for the registration eligibility + demographic capture stage.
 * Flipping it off makes the completion read report the stage not required, so
 * the mobile launch gate routes straight past registration without stamping
 * anything; turning it back on re-gates members who never completed the stage.
 *
 * Default OFF while the stage is held off member-facing builds: the default is
 * what a failed PostHog evaluation serves, and a fallback must not reverse the
 * deliberately-off flag and gate members into a flow product has not released.
 * Revisit the fail direction when the stage launches for real, when silently
 * skipping eligibility becomes the worse failure mode.
 */
export const registrationEligibilityFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-registration-eligibility',
  display_name: 'Registration eligibility stage',
  active_on_create: true,
  default_value: false,
  internal_only: false,
});

/**
 * Measurement provenance, never behaviour: true for members of the
 * ops-maintained "Internal / Test users (explicit)" cohort in the env-matching
 * PostHog project (each project's flag carries one 100%-rollout release
 * condition over that project's cohort). Experiment emitters read it from the
 * evaluateAll snapshot they already fetch and stamp `is_qa` with it, so
 * internal testers drop out of readouts without a deploy when the cohort
 * changes. Default false is load-bearing: a failed or default-substituted
 * evaluation must count the member — an outage must never silently shrink
 * experiment samples. Static-cohort targeting resolves only on the provider's
 * remote evaluation path, which is the production configuration.
 */
export const internalMemberFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-internal-member',
  display_name: 'Internal member (QA measurement provenance)',
  active_on_create: true,
  default_value: false,
  internal_only: true,
});

/**
 * Server-side enforcement of the Sona AI consent: when on, every Sona
 * conversational entry point refuses to mint for a member whose latest
 * `ai_sona` consent is not granted. Default OFF so the consent record, gate
 * screen and Settings toggle can land before the final legal copy is signed
 * off; flipping it on is a flag change, not a deploy. Member-visible: the
 * standing read reports `enforced` so clients can explain a refusal.
 */
export const sonaConsentEnforcementFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-sona-consent-enforcement',
  display_name: 'Sona AI consent enforcement',
  active_on_create: true,
  default_value: false,
  internal_only: false,
});

/**
 * Lifts the server-side AI restriction for members in an opioid use disorder
 * program: when explicitly `true` for a member, an active OUD enrollment no
 * longer makes their AI availability `restricted`. Default OFF, and every
 * failure path reads as off, so OUD members stay restricted until the flag is
 * deliberately turned on. Independent of the restricted-state list, which is
 * code config. Server-only (evaluated at Sona mint and availability read).
 */
export const sonaForOudMembersFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-sona-for-oud-members',
  display_name: 'Sona for OUD-program members (lifts the OUD AI restriction)',
  active_on_create: true,
  default_value: false,
  internal_only: true,
});

// Greeting experiment: A/B tests whether member-centred (momentum) or
// agent-centred (settled) first-message framing leads to better session
// outcomes. Server-only (resolved at voice-session mint). The default names
// the control arm only because a variant flag must default to a declared
// variant: a default-substituted evaluation (outage, flag inactive) never
// enrols the member, and the session plays the static first_message.
export const journeySonaGreetingFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-journey-sona-greeting-variant',
  display_name: 'Journey-sona greeting experiment (settled vs momentum)',
  active_on_create: true,
  default_value: 'settled',
  internal_only: true,
  variants: ['settled', 'momentum'],
});

export const baseSonaGreetingFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-base-sona-greeting-variant',
  display_name: 'Base-sona greeting experiment (settled vs momentum)',
  active_on_create: true,
  default_value: 'settled',
  internal_only: true,
  variants: ['settled', 'momentum'],
});

// Server-authoritative kill-switch for the per-call voice-agent model override.
// Default-on: the minter names the conversational model on every in-scope call,
// and a PostHog outage or absent config resolves to `true` so a provider problem
// can never silently move calls back to the worker's baked model. Only an
// explicit `false` drops the override. Internal-only (a server-side decision,
// never a client fetch).
export const voiceAgentLlmOverrideFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-voice-agent-llm-override',
  display_name: 'Voice agent LLM override kill-switch',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});

// Server-side reasoning effort for the same per-call override, so an operator
// can change it per environment without a deploy. The variants mirror the
// agent worker's effort allow-list; an outage, an inactive flag or a value
// outside them resolves to the default. Only read when the kill switch above
// leaves the override on.
export const voiceAgentReasoningEffortFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-voice-agent-reasoning-effort',
  display_name: 'Voice agent reasoning effort',
  active_on_create: true,
  default_value: 'low',
  internal_only: true,
  variants: ['none', 'low', 'medium', 'high'],
});

// Backend operator controls. Repository defaults keep unconfigured environments
// enabled; the active release conditions remain owned by PostHog operators.
export const pushCommitmentFollowUpFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-commitment-follow-up',
  display_name: 'Push: Sona commitment follow-up',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushReminderNudgeFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-reminder-nudge',
  display_name: 'Push: check-in reminder nudge',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushCareChatFirstMessageFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-care-chat-first-message',
  display_name: 'Push: automated care-chat companion',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushCareChatMessageFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-care-chat-message',
  display_name: 'Push: human care-chat message',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushTelemedicineCallReminderFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-telemedicine-call-reminder',
  display_name: 'Push: telemedicine call reminder',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushTelemedicineCallStartedFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-telemedicine-call-started',
  display_name: 'Push: telemedicine call started',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});
export const pushOnboardingDayFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-push-onboarding-day',
  display_name: 'Push: onboarding day (dormant)',
  active_on_create: true,
  default_value: true,
  internal_only: true,
});

export const pushFeatureFlags = [
  pushCommitmentFollowUpFlag,
  pushReminderNudgeFlag,
  pushCareChatFirstMessageFlag,
  pushCareChatMessageFlag,
  pushTelemedicineCallReminderFlag,
  pushTelemedicineCallStartedFlag,
  pushOnboardingDayFlag,
] as const satisfies readonly FeatureFlagDefinition[];

// Chooses which welcome series a member's first composed day is built from:
// `true` composes the replacement series, anything else the current trio.
// Server-only, evaluated once per member by the journey composer; a PostHog
// outage, absent config or inactive flag resolves to `false`, so a provider
// problem can only ever leave a member on the current series.
export const welcomeSeriesV2Flag = featureFlagDefinitionSchema.parse({
  key: 'flag-welcome-series-v2',
  display_name: 'Replacement welcome series (journey composer)',
  active_on_create: false,
  default_value: false,
  internal_only: true,
});

// Off until a human starts a percentage rollout. Missing or failed evaluation keeps control.
export const intakePhasedRolloutFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-intake-phased-rollout',
  display_name: 'Phased intake rollout',
  active_on_create: false,
  default_value: false,
  internal_only: true,
});

// Environment-wide operator switch for the daily memory cleanup. No member
// cohorts or percentage rollout: one synthetic job identity gates the whole sweep.
export const memoryLifecycleCleanupFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-memory-lifecycle-cleanup',
  display_name: 'Memory lifecycle cleanup',
  active_on_create: false,
  default_value: false,
  internal_only: true,
});

export const sonaFollowUpWriterFlag = featureFlagDefinitionSchema.parse({
  key: 'flag-sona-follow-up-writer',
  display_name: 'Screened model-written Sona follow-ups',
  active_on_create: false,
  default_value: false,
  internal_only: true,
});

const registry = [
  sonaFollowUpWriterFlag,
  analyticsDiagnosticsFlag,
  careCallFollowupFlag,
  rewardsEnrolmentFlag,
  initialReviewEntryFlag,
  registrationEligibilityFlag,
  internalMemberFlag,
  sonaConsentEnforcementFlag,
  sonaForOudMembersFlag,
  journeySonaGreetingFlag,
  baseSonaGreetingFlag,
  voiceAgentLlmOverrideFlag,
  voiceAgentReasoningEffortFlag,
  intakePhasedRolloutFlag,
  ...pushFeatureFlags,
  welcomeSeriesV2Flag,
  memoryLifecycleCleanupFlag,
] as const satisfies readonly FeatureFlagDefinition[];

export function listFeatureFlagDefinitions(): readonly FeatureFlagDefinition[] {
  return registry;
}

export function registeredFeatureFlagKeys(): readonly FeatureFlagKey[] {
  return registry.map(({ key }) => key);
}

export function featureFlagDefaults(): Readonly<
  Record<string, FeatureFlagDefinition['default_value']>
> {
  return Object.fromEntries(registry.map(({ key, default_value }) => [key, default_value]));
}

export function featureFlagEvaluationVariants(
  definition: FeatureFlagDefinition,
): readonly string[] {
  const variants = new Set<string>(definition.variants ?? []);
  if (typeof definition.default_value === 'boolean') {
    variants.add('enabled');
    variants.add('disabled');
  }
  return [...variants];
}

export function assertFeatureFlagVariants(
  definition: FeatureFlagDefinition,
  expectedVariants: readonly string[],
): void {
  if (
    !definition.variants ||
    definition.variants.length !== expectedVariants.length ||
    definition.variants.some((variant, index) => variant !== expectedVariants[index])
  ) {
    throw new Error(`Experiment variants do not match registered flag ${definition.key}`);
  }
}

export function buildFeatureFlagEvaluationEvents(snapshot: {
  evaluations: Readonly<
    Record<
      string,
      {
        value: boolean | string;
        source: 'server' | 'default';
        usedDefault: boolean;
      }
    >
  >;
}): ProductEvent[] {
  return Object.entries(snapshot.evaluations).flatMap(([key, evaluation]) => {
    const definition = registry.find(({ key: registeredKey }) => registeredKey === key);
    if (!definition) return [];
    let variant: string;
    if (typeof evaluation.value === 'boolean') {
      variant = evaluation.value ? 'enabled' : 'disabled';
    } else {
      const parsedVariant = variantKeySchema.safeParse(evaluation.value);
      if (!parsedVariant.success) return [];
      variant = parsedVariant.data;
    }
    if (!featureFlagEvaluationVariants(definition).includes(variant)) return [];
    return [
      buildFeatureFlagEvaluatedEvent({
        flag_key: definition.key,
        variant_key: variant,
        evaluation_source: evaluation.source,
        used_default: evaluation.usedDefault,
      }),
    ];
  });
}

export function buildContextFeatureFlagEvaluationEvents(
  context: Pick<AnalyticsContext, 'flag_evaluations'>,
): ProductEvent[] {
  const evaluations: Record<
    string,
    { value: boolean | string; source: 'server' | 'default'; usedDefault: boolean }
  > = {};
  for (const [key, evaluation] of Object.entries(context.flag_evaluations)) {
    if (!evaluation) continue;
    evaluations[key] = {
      value: evaluation.value,
      source: evaluation.source,
      usedDefault: evaluation.used_default,
    };
  }
  return buildFeatureFlagEvaluationEvents({
    evaluations,
  });
}
